// Utility function to format numbers based on value type (e.g., percentage, thousands, millions)
export const formatValue = (num, valueType) => {
  // Handle undefined, null, or non-numeric values
  if (num === undefined || num === null || isNaN(num) || num === 0) {
    return '';
  }

  // Convert to number if it's a string
  const value = typeof num === 'string' ? parseFloat(num) : num;

  if (valueType === 'percentage') {
    return `${value}%`;
  } else {
    let formattedNum;
    if (value >= 1_000_000) {
      const divided = value / 1_000_000;
      formattedNum = Number.isInteger(divided) ? `${divided}m` : `${divided.toFixed(1)}m`;
    } else if (value >= 1_000) {
      const divided = value / 1_000;
      formattedNum = Number.isInteger(divided) ? `${divided}k` : `${divided.toFixed(1)}k`;
    } else {
      formattedNum = value.toString();
    }
    return formattedNum;
  }
};

// Special formatter for interaction and actor buttons that shows 0 instead of empty
export const formatButtonValue = (num, valueType) => {
  // Handle undefined, null, or non-numeric values
  if (num === undefined || num === null || isNaN(num)) {
    return '0';
  }

  // Convert to number if it's a string
  const value = typeof num === 'string' ? parseFloat(num) : num;

  if (valueType === 'percentage') {
    return `${value}%`;
  } else {
    let formattedNum;
    if (value >= 1_000_000) {
      const divided = value / 1_000_000;
      formattedNum = Number.isInteger(divided) ? `${divided}m` : `${divided.toFixed(1)}m`;
    } else if (value >= 1_000) {
      const divided = value / 1_000;
      formattedNum = Number.isInteger(divided) ? `${divided}k` : `${divided.toFixed(1)}k`;
    } else {
      formattedNum = value.toString();
    }
    return formattedNum;
  }
};

// Utility function to determine contrasting text color based on background color
export const getContrastingTextColor = (hexColor) => {
  if (!hexColor) {
    return getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim() || '#000000';
  }
  
  try {
    hexColor = hexColor.replace('#', '');
    // Handle 3-digit hex colors
    if (hexColor.length === 3) {
      hexColor = hexColor.split('').map(char => char + char).join('');
    }
    // Handle invalid hex colors
    if (hexColor.length !== 6) {
      return getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim() || '#000000';
    }
    
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);
    
    // Handle invalid RGB values
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim() || '#000000';
    }
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Use CSS variables for light and dark text colors
    const darkText = getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim() || '#000000';
    const lightText = getComputedStyle(document.documentElement).getPropertyValue('--color-background-paper').trim() || '#FFFFFF';
    
    return luminance > 0.5 ? darkText : lightText;
  } catch (error) {
    console.error('Error calculating contrasting text color:', error);
    return getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim() || '#000000';
  }
};

// Utility function to sanitize tooltip content to prevent XSS attacks
export const sanitizeTooltipContent = (content) => {
  const tempDiv = document.createElement('div');
  tempDiv.textContent = content;
  return tempDiv.innerHTML;
};

// Utility function to format dates for display
export const formatDate = (dateStr) => {
  if (!dateStr) return ''; // Return empty string if no date provided
  
  try {
    // Try to parse the date string
    const date = new Date(dateStr);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateStr);
      return '';
    }
    
    return new Intl.DateTimeFormat('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return ''; // Return empty string on error
  }
};